// ----------------------------------------------------------------------------
// Plan limit. Shown when creating a service is blocked by the church's plan
// (HTTP 402). Explains the limit and offers two ways forward: delete an
// existing service to free a slot, or upgrade.
// ----------------------------------------------------------------------------

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { ServicePlan } from '@app/types';
import { Banner, Button, Modal } from '@app/components/ui';
import { monthDayYear } from '@app/lib/format';

interface PlanLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Friendly, actionable message from the server (falls back to a generated one). */
  message: string;
  /** Plan cap for the church's current tier (for display). */
  maxPlans: number;
  /** Current tier name, already human-cased where possible (e.g. "Free"). */
  tier: string;
  /** Active service plans the admin can delete to free a slot. */
  plans: ServicePlan[];
  /** Soft-deletes a plan. Resolves once the plan is removed. */
  onDeletePlan: (planId: string) => Promise<void>;
  /** Re-attempts creating the service the admin was trying to add. */
  onRetry: () => void;
  /** Navigates to the plan / upgrade page. */
  onUpgrade: () => void;
}

const planDate = (date: string) => (Number.isNaN(new Date(date).getTime()) ? '' : monthDayYear(date));

export function PlanLimitModal({ isOpen, onClose, message, maxPlans, tier, plans, onDeletePlan, onRetry, onUpgrade }: PlanLimitModalProps) {
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletedCount, setDeletedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Deleting any plan frees exactly one church-wide slot, so once the admin has
  // removed at least one, retrying the create is guaranteed to have room.
  const canRetry = deletedCount > 0;

  const handleDelete = async (planId: string) => {
    setDeletingId(planId);
    setError(null);
    try {
      await onDeletePlan(planId);
      setConfirmingId(null);
      setDeletedCount((c) => c + 1);
    } catch {
      setError('Could not delete that plan. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} title="Service plan limit reached" width={500}>
      <div className="flex flex-col gap-4">
        <Banner tone="amb">
          <span data-testid="plan-limit-message">{message}</span>
        </Banner>

        <div>
          <div className="wp-eyebrow mb-2">
            Your services ({plans.length} of {maxPlans} on {tier})
          </div>
          {plans.length === 0 ? (
            <p className="text-[13.5px] text-t4">No service plans to remove.</p>
          ) : (
            <ul className="max-h-60 overflow-y-auto rounded-[16px] border border-line">
              {plans.map((plan) => (
                <li key={plan.id} data-testid="plan-limit-row" className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-b border-line last:border-0">
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold truncate">{plan.title || 'Untitled service'}</div>
                    <div className="text-[12.5px] text-t4">{planDate(plan.date)}</div>
                  </div>
                  {confirmingId === plan.id ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        className="!h-[30px] !text-[12.5px]"
                        data-testid={`plan-limit-confirm-delete-${plan.id}`}
                        disabled={deletingId === plan.id}
                        onClick={() => handleDelete(plan.id)}
                      >
                        {deletingId === plan.id ? 'Deleting…' : 'Confirm'}
                      </Button>
                      <Button variant="ghost" size="sm" className="!h-[30px] !text-[12.5px]" onClick={() => setConfirmingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-testid={`plan-limit-delete-${plan.id}`}
                      onClick={() => setConfirmingId(plan.id)}
                      aria-label={`Delete ${plan.title || 'service'}`}
                      className="w-8 h-8 rounded-full text-t4 hover:text-acc hover:bg-acc-bg flex items-center justify-center shrink-0"
                    >
                      <Trash2 size={15} aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <p data-testid="plan-limit-error" role="alert" className="text-[13px] text-acc">
            {error}
          </p>
        )}

        <div className="flex gap-2.5 mt-1">
          <Button variant="ghost" shape="form" size="lg" data-testid="plan-limit-upgrade" onClick={onUpgrade}>
            Upgrade plan
          </Button>
          <Button shape="form" size="lg" className="flex-1" data-testid="plan-limit-retry" disabled={!canRetry} onClick={onRetry}>
            Try again
          </Button>
        </div>
      </div>
    </Modal>
  );
}
